import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCalendar, FiClock, FiHome, FiUser, FiCreditCard, FiCheckCircle, FiArrowLeft, FiArrowRight } = FiIcons;

const Booking = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingData, setBookingData] = useState({
    service: '',
    frequency: '',
    date: '',
    time: '',
    duration: '',
    address: '',
    bedrooms: '',
    bathrooms: '',
    extras: [],
    customerInfo: {
      name: '',
      email: '',
      phone: ''
    },
    specialInstructions: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    { id: 'residential', name: 'Residential Cleaning', price: 80, duration: '2-3 hours' },
    { id: 'deep', name: 'Deep Cleaning', price: 200, duration: '4-6 hours' },
    { id: 'commercial', name: 'Commercial Cleaning', price: 150, duration: '3-4 hours' },
    { id: 'moveout', name: 'Move-in/Move-out', price: 250, duration: '5-7 hours' }
  ];

  const frequencies = [
    { id: 'once', name: 'One-time', discount: 0 },
    { id: 'weekly', name: 'Weekly', discount: 15 },
    { id: 'biweekly', name: 'Bi-weekly', discount: 10 },
    { id: 'monthly', name: 'Monthly', discount: 5 }
  ];

  const timeSlots = [
    '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM'
  ];

  const extras = [
    { id: 'windows', name: 'Window Cleaning', price: 25 },
    { id: 'oven', name: 'Oven Cleaning', price: 30 },
    { id: 'fridge', name: 'Refrigerator Cleaning', price: 20 },
    { id: 'garage', name: 'Garage Cleaning', price: 40 },
    { id: 'basement', name: 'Basement Cleaning', price: 35 }
  ];

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBookingData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBookingData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleExtraToggle = (extraId) => {
    setBookingData(prev => ({
      ...prev,
      extras: prev.extras.includes(extraId)
        ? prev.extras.filter(id => id !== extraId)
        : [...prev.extras, extraId]
    }));
  };

  const calculateTotal = () => {
    const selectedService = services.find(s => s.id === bookingData.service);
    if (!selectedService) return 0;

    let total = selectedService.price;
    
    // Add extras
    const extrasTotal = bookingData.extras.reduce((sum, extraId) => {
      const extra = extras.find(e => e.id === extraId);
      return sum + (extra ? extra.price : 0);
    }, 0);
    total += extrasTotal;

    // Apply frequency discount
    const frequency = frequencies.find(f => f.id === bookingData.frequency);
    if (frequency && frequency.discount > 0) {
      total = total * (1 - frequency.discount / 100);
    }

    return total;
  };

  const nextStep = () => {
    if (currentStep < 4) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="pt-20 min-h-screen bg-gray-50 flex items-center justify-center"
      >
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-2xl shadow-lg">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <SafeIcon icon={FiCheckCircle} className="w-20 h-20 text-green-500 mx-auto mb-6" />
          </motion.div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Confirmed!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for choosing CleanPro. We'll send you a confirmation email shortly 
            with all the details of your appointment.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Booking Reference: #CP-{Math.random().toString(36).substr(2, 9).toUpperCase()}
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setCurrentStep(1);
              setBookingData({
                service: '',
                frequency: '',
                date: '',
                time: '',
                duration: '',
                address: '',
                bedrooms: '',
                bathrooms: '',
                extras: [],
                customerInfo: { name: '', email: '', phone: '' },
                specialInstructions: ''
              });
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
          >
            Book Another Service
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20 min-h-screen bg-gray-50"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${
                  step <= currentStep
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {step}
              </div>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            ></div>
          </div>
          <div className="flex justify-between mt-2 text-sm text-gray-600">
            <span>Service</span>
            <span>Schedule</span>
            <span>Details</span>
            <span>Confirm</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {/* Step 1: Service Selection */}
          {currentStep === 1 && (
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Select Your Service</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => handleInputChange('service', service.id)}
                    className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                      bookingData.service === service.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.name}</h3>
                    <p className="text-2xl font-bold text-blue-600 mb-2">Starting at ${service.price}</p>
                    <p className="text-gray-600">{service.duration}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Frequency</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {frequencies.map((freq) => (
                    <div
                      key={freq.id}
                      onClick={() => handleInputChange('frequency', freq.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer text-center transition-all ${
                        bookingData.frequency === freq.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <p className="font-semibold text-gray-900">{freq.name}</p>
                      {freq.discount > 0 && (
                        <p className="text-sm text-green-600">Save {freq.discount}%</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Schedule */}
          {currentStep === 2 && (
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Schedule Your Service</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Date
                  </label>
                  <input
                    type="date"
                    value={bookingData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Time
                  </label>
                  <select
                    value={bookingData.time}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select time</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Address
                </label>
                <textarea
                  value={bookingData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter your full address including apartment/unit number"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bedrooms
                  </label>
                  <select
                    value={bookingData.bedrooms}
                    onChange={(e) => handleInputChange('bedrooms', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="1">1 Bedroom</option>
                    <option value="2">2 Bedrooms</option>
                    <option value="3">3 Bedrooms</option>
                    <option value="4">4 Bedrooms</option>
                    <option value="5+">5+ Bedrooms</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bathrooms
                  </label>
                  <select
                    value={bookingData.bathrooms}
                    onChange={(e) => handleInputChange('bathrooms', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="1">1 Bathroom</option>
                    <option value="1.5">1.5 Bathrooms</option>
                    <option value="2">2 Bathrooms</option>
                    <option value="2.5">2.5 Bathrooms</option>
                    <option value="3">3 Bathrooms</option>
                    <option value="3+">3+ Bathrooms</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Details & Extras */}
          {currentStep === 3 && (
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Additional Details</h2>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Add-on Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {extras.map((extra) => (
                    <div
                      key={extra.id}
                      onClick={() => handleExtraToggle(extra.id)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        bookingData.extras.includes(extra.id)
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-gray-900">{extra.name}</span>
                        <span className="font-semibold text-blue-600">+${extra.price}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={bookingData.customerInfo.name}
                    onChange={(e) => handleInputChange('customerInfo.name', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={bookingData.customerInfo.email}
                    onChange={(e) => handleInputChange('customerInfo.email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={bookingData.customerInfo.phone}
                    onChange={(e) => handleInputChange('customerInfo.phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Special Instructions (Optional)
                </label>
                <textarea
                  value={bookingData.specialInstructions}
                  onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                  placeholder="Any special instructions or areas that need extra attention..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                ></textarea>
              </div>
            </motion.div>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 4 && (
            <motion.div
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Confirm Your Booking</h2>
              
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Booking Summary</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-medium">
                      {services.find(s => s.id === bookingData.service)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frequency:</span>
                    <span className="font-medium">
                      {frequencies.find(f => f.id === bookingData.frequency)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date & Time:</span>
                    <span className="font-medium">{bookingData.date} at {bookingData.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Property:</span>
                    <span className="font-medium">{bookingData.bedrooms} bed, {bookingData.bathrooms} bath</span>
                  </div>
                  {bookingData.extras.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Add-ons:</span>
                      <span className="font-medium">
                        {bookingData.extras.map(extraId => 
                          extras.find(e => e.id === extraId)?.name
                        ).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total:</span>
                      <span className="text-blue-600">${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• You'll receive a confirmation email with all details</li>
                  <li>• Our team will call you 24 hours before the appointment</li>
                  <li>• Payment is collected after the service is completed</li>
                  <li>• 100% satisfaction guarantee on all our work</li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              <span>Previous</span>
            </button>

            {currentStep < 4 ? (
              <button
                onClick={nextStep}
                disabled={
                  (currentStep === 1 && (!bookingData.service || !bookingData.frequency)) ||
                  (currentStep === 2 && (!bookingData.date || !bookingData.time || !bookingData.address)) ||
                  (currentStep === 3 && (!bookingData.customerInfo.name || !bookingData.customerInfo.email))
                }
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-semibold transition-all ${
                  (currentStep === 1 && (!bookingData.service || !bookingData.frequency)) ||
                  (currentStep === 2 && (!bookingData.date || !bookingData.time || !bookingData.address)) ||
                  (currentStep === 3 && (!bookingData.customerInfo.name || !bookingData.customerInfo.email))
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:scale-105'
                }`}
              >
                <span>Next</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all"
              >
                <SafeIcon icon={FiCheckCircle} className="w-5 h-5" />
                <span>Confirm Booking</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Booking;